import React, { useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { Button, Input } from 'reactstrap';
import { timeZoneLabel } from 'fym-shared/src/music24/timeZones';
import { TypedMemo } from '../../../../common/types';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../../common/views/LoadingView';
import { Panel, PanelBody, PanelHead } from '../../../../music24/components/primitives/Panel';
import { SettingsSaveStatus } from '../../SettingsSaveStatus';
import { timeZoneMessages } from './timeZoneMessages';
import { useTimeZoneDraft } from './useTimeZoneDraft';

interface Props {
  accountId: number;
  /** The demo artist: the row shows its zone and offers no change. */
  readOnly: boolean;
}

/**
 * Settings → Artist → Time zone. It uses the row pattern of Settings → Account → Language
 * (design settings-redesign b1: a panel head with the save status, one control in the body), so
 * it needs no own design frame. With no stored zone it suggests the zone of this device and saves
 * nothing until the user clicks Save.
 */
const ArtistTimeZoneCardComponent: React.FC<Props> = ({ accountId, readOnly }) => {
  const intl = useIntl();
  const selectId = useId();
  const draft = useTimeZoneDraft(accountId);
  const { refetch, submit, setPicked, device } = draft;

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const onSave = useCallback(() => {
    submit().catch(console.error);
  }, [submit]);
  const pickDevice = useCallback(() => {
    if (device !== null) {
      setPicked(device);
    }
  }, [device, setPicked]);

  const status =
    draft.stored !== null
      ? intl.formatMessage(timeZoneMessages.current, { zone: timeZoneLabel(draft.stored) })
      : device !== null
        ? intl.formatMessage(timeZoneMessages.notSet, { zone: timeZoneLabel(device) })
        : intl.formatMessage(timeZoneMessages.notSetNoDevice);

  return (
    <Panel as="section" data-testid="artist-time-zone">
      <PanelHead
        title={
          <label htmlFor={selectId} className="mb-0">
            {intl.formatMessage(timeZoneMessages.title)}
          </label>
        }
        subtitle={intl.formatMessage(timeZoneMessages.hint)}
        right={<SettingsSaveStatus state={draft.saveState} />}
      />
      <PanelBody>
        {draft.loadError !== undefined ? (
          <ErrorView error={draft.loadError} retry={retry} />
        ) : draft.isLoading ? (
          <LoadingView />
        ) : (
          <div className="d-flex flex-column gap-2">
            <p className="mb-0" data-testid="artist-time-zone-status">
              {status}
            </p>
            {!readOnly && (
              <>
                <Input
                  type="search"
                  aria-label={intl.formatMessage(timeZoneMessages.search)}
                  placeholder={intl.formatMessage(timeZoneMessages.searchPlaceholder)}
                  value={draft.query}
                  onChange={(event) => draft.setQuery(event.target.value)}
                />
                <select
                  id={selectId}
                  className="form-select"
                  value={draft.picked}
                  disabled={draft.isSaving}
                  onChange={(event) => setPicked(event.target.value)}
                >
                  {draft.options.map((option) => (
                    <option key={option.zone} value={option.zone}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {draft.noMatch && (
                  <small className="text-body-secondary">
                    {intl.formatMessage(timeZoneMessages.noMatch, { query: draft.query.trim() })}
                  </small>
                )}
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Button color="primary" disabled={!draft.canSave} onClick={onSave}>
                    {intl.formatMessage(timeZoneMessages.save)}
                  </Button>
                  {device !== null && draft.picked !== device && (
                    <Button color="link" className="px-0" onClick={pickDevice}>
                      {intl.formatMessage(timeZoneMessages.useDevice, { zone: device })}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const ArtistTimeZoneCard = TypedMemo(ArtistTimeZoneCardComponent);
